import React from 'react';
import * as XLSX from 'xlsx';

function useExportExcel() {
  const handleExportExcel = () => {
    console.log("Exporting Excel");
  };

  const handleExportToExcel = (data, selectedRows) => {
    console.log("Exporting data to Excel", data);
    console.log("Selected rows", selectedRows);

    // Filter data based on selectedRows
    const filteredData = selectedRows && selectedRows.length > 0
      ? data.filter(item => selectedRows.includes(item.id))
      : data;
    
    // Get all unique categories from filtered invoices
    const allCategories = new Set();
    filteredData.forEach(item => {
      item.invoiceCategories?.forEach(cat => {
        if (cat.category?.name) {
          allCategories.add(cat.category.name);
        }
      });
    });
    
    // Format the data for export with categories
    const formattedData = filteredData.map(item => {
      // Extract nested objects
      const governorateName = item.governorate?.name || '';
      const representativeName = item.representative?.fullName || '';
      
      // Format dates
      const invoiceDate = item.invoiceDate ? new Date(item.invoiceDate).toLocaleDateString('ar-EG') : '';
      
      // Create base object with essential fields
      const baseObj = {
        'رقم الفاتورة': item.invoiceCode || '',
        'تاريخ الفاتورة': invoiceDate,
        'العميل': item.customer || '',
        'المحافظة': governorateName, 
        "العنوان": item.address || '',
        'الهاتف': item.phone || '',
        'المندوب': representativeName,
        'الحالة': item.status || '',
        'الإجمالي': item.totalPrice || 0,
        'الشحن': item.shipping || 0,
        'متأخر': item.isLate ? 'نعم' : 'لا',
        'عاجل': item.isUrgent ? 'نعم' : 'لا'
      };
      
      // Add category quantities
      allCategories.forEach(categoryName => {
        const category = item.invoiceCategories?.find(cat => cat.category?.name === categoryName);
        baseObj[categoryName] = category ? category.quantity : 0;
      });
      
      return baseObj;
    });
    
    // Calculate total quantities for each category and grand total
    const categoryTotals = {};
    let grandTotal = 0;
    
    allCategories.forEach(categoryName => {
      const total = formattedData.reduce((sum, item) => sum + (parseFloat(item[categoryName]) || 0), 0);
      categoryTotals[categoryName] = total;
      grandTotal += total;
    });
    
    // Add a summary row at the bottom
    const summaryRow = {
      'رقم الفاتورة': 'الإجمالي',
      'تاريخ الفاتورة': '',
      'العميل': '',
      'المحافظة': '',
      'الهاتف': '',
      'المندوب': '',
      'الحالة': '',
      'الإجمالي': formattedData.reduce((sum, item) => sum + (parseFloat(item['الإجمالي']) || 0), 0),
      'الشحن': formattedData.reduce((sum, item) => sum + (parseFloat(item['الشحن']) || 0), 0),
      'متأخر': '',
      'عاجل': '',
      ...categoryTotals,
      'إجمالي الكميات': grandTotal
    };
    
    // Add the summary row to the data
    formattedData.push(summaryRow);
    
    // Create worksheet with the formatted data
    const ws = XLSX.utils.json_to_sheet(formattedData);
    
    // Set column widths for better readability
    const colWidths = [
      { wch: 12 },  // رقم الفاتورة
      { wch: 12 },  // تاريخ الفاتورة
      { wch: 25 },  // العميل
      { wch: 15 },  // المحافظة
      { wch: 15 },  // الهاتف
      { wch: 20 },  // المندوب
      { wch: 15 },  // الحالة
      { wch: 10 },  // الإجمالي
      { wch: 10 },  // الشحن
      { wch: 8 },   // متأخر
      { wch: 8 },   // عاجل
      ...Array.from(allCategories).map(() => ({ wch: 15 })), // Category columns
      { wch: 15 }   // إجمالي الكميات
    ];
    
    ws['!cols'] = colWidths;
    
    // Apply table styling
    const range = XLSX.utils.decode_range(ws['!ref']);
    const lastRow = range.e.r;
    
    // Style the header row
    for (let col = range.s.c; col <= range.e.c; col++) {
      const headerCell = XLSX.utils.encode_cell({ r: 0, c: col });
      if (!ws[headerCell]) ws[headerCell] = { v: '' };
      ws[headerCell].s = {
        font: { bold: true, color: { rgb: "FFFFFF" } },
        fill: { fgColor: { rgb: "4F81BD" } },
        alignment: { horizontal: "center", vertical: "center" },
        border: {
          top: { style: "thin", color: { rgb: "000000" } },
          bottom: { style: "thin", color: { rgb: "000000" } },
          left: { style: "thin", color: { rgb: "000000" } },
          right: { style: "thin", color: { rgb: "000000" } }
        }
      };
    }
    
    // Style the data rows
    for (let row = range.s.r + 1; row < lastRow; row++) {
      for (let col = range.s.c; col <= range.e.c; col++) {
        const cell = XLSX.utils.encode_cell({ r: row, c: col });
        if (!ws[cell]) ws[cell] = { v: '' };
        ws[cell].s = {
          alignment: { horizontal: "center", vertical: "center" },
          border: {
            top: { style: "thin", color: { rgb: "000000" } },
            bottom: { style: "thin", color: { rgb: "000000" } },
            left: { style: "thin", color: { rgb: "000000" } },
            right: { style: "thin", color: { rgb: "000000" } }
          }
        };
      }
    }
    
    // Style the summary row
    for (let col = range.s.c; col <= range.e.c; col++) {
      const summaryCell = XLSX.utils.encode_cell({ r: lastRow, c: col });
      if (!ws[summaryCell]) ws[summaryCell] = { v: '' };
      ws[summaryCell].s = {
        font: { bold: true },
        fill: { fgColor: { rgb: "E2EFDA" } },
        alignment: { horizontal: "center", vertical: "center" },
        border: {
          top: { style: "thin", color: { rgb: "000000" } },
          bottom: { style: "thin", color: { rgb: "000000" } },
          left: { style: "thin", color: { rgb: "000000" } },
          right: { style: "thin", color: { rgb: "000000" } }
        }
      };
    }
    
    // Create workbook and append the worksheet
    const wb = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(wb, ws, "فواتير");
    
    // Write the file
    XLSX.writeFile(wb, "فواتير.xlsx");
  };
  
  return {
    handleExportExcel,
    handleExportToExcel
  };
}

export default useExportExcel;
